// Helpers for the Brand Theme color fields (primary / secondary).
//
// The native <input type="color"> swatch only understands a 6-digit
// `#rrggbb` hex, but we want owners to be able to copy/paste any common
// color string — short hex (#abc), 8-digit hex with alpha (#aabbccdd) or
// rgb()/rgba() — straight into the text box alongside the picker.
//
//   isValidColor — gates the typed/pasted value so we can show an error.
//   toHexColor   — normalises whatever was pasted down to `#rrggbb` so the
//                  swatch + native picker stay in sync with the text.

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(0|1|0?\.\d+|1\.0+)\s*)?\)$/i;

// True when `value` is a hex (#rgb/#rgba/#rrggbb/#rrggbbaa) or rgb()/rgba()
// color whose channels are within range. Empty/non-string is not valid.
export const isValidColor = (value) => {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (HEX_RE.test(v)) return true;
  const m = RGB_RE.exec(v);
  if (!m) return false;
  return [m[1], m[2], m[3]].every((n) => Number(n) >= 0 && Number(n) <= 255);
};

const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));
const toByteHex = (n) => clampByte(n).toString(16).padStart(2, "0");

// Normalise any supported color string to a 6-digit `#rrggbb` hex (alpha is
// dropped — the native picker has no alpha channel). Returns `fallback` for
// anything we can't parse so the picker always has a usable value.
export const toHexColor = (value, fallback = "#000000") => {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  if (HEX_RE.test(v)) {
    const hex = v.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      // Expand shorthand (#abc -> #aabbcc), drop any 4th alpha nibble.
      const expanded = hex
        .slice(0, 3)
        .split("")
        .map((c) => c + c)
        .join("");
      return ("#" + expanded).toLowerCase();
    }
    // 6 or 8 digit — keep the first 6 (rgb), drop alpha.
    return ("#" + hex.slice(0, 6)).toLowerCase();
  }
  const m = RGB_RE.exec(v);
  if (m) {
    return ("#" + toByteHex(Number(m[1])) + toByteHex(Number(m[2])) + toByteHex(Number(m[3]))).toLowerCase();
  }
  return fallback;
};
